import { Button, useTranslation } from '@hbs-us/ui';

import { resolveHomeLayout } from '../../auth/landing.js';
import { useSession } from '../../auth/useSession.js';
import { RouteSkeleton } from '../_ops/RouteSkeleton.js';

import { CrewHomeView } from './CrewHomeView.js';
import { DispatcherHomeView } from './DispatcherHomeView.js';
import { HomeView } from './HomeView.js';
import { useOverviewQuery } from './useOverviewQuery.js';

/**
 * The `/` index route component — one route, a layout per viewer.
 *
 * Home used to be a single screen fed by a single request, `GET /api/v1/overview`, which is guarded
 * by `reports.view`. Anybody without that key was redirected away from Home entirely and never had
 * one. The layout is now chosen from what the viewer can read, BEFORE any request is made, so a
 * crew member gets a Home built from the endpoints they actually hold.
 *
 * Choosing the layout here rather than inside a view is what keeps the guaranteed-403 impossible:
 * `useOverviewQuery` is mounted only on the branch that holds `reports.view`, so the request is
 * never fired by somebody who is going to be refused.
 */
export function HomeRoute() {
  const { session } = useSession();
  const layout = resolveHomeLayout(session?.user ?? null);

  if (layout === 'crew') return <CrewHomeView />;
  if (layout === 'dispatcher') return <DispatcherHomeView />;

  // 'owner', and also the unreachable `null`: the index route's `beforeLoad` redirects anybody Home
  // has no layout for, so nothing without `reports.view` renders this branch. Falling through to
  // the owner layout rather than throwing keeps a routing mistake looking like a failed request
  // instead of a blank screen.
  return <OwnerHome />;
}

/** The KPI dashboard, for a viewer who holds `reports.view`. One coarse request, no re-aggregation. */
function OwnerHome() {
  const { t } = useTranslation();
  const query = useOverviewQuery();

  if (query.isPending) {
    // The shape of the screen, not a spinner on an empty field. Home has no filter row and no
    // header action, so it stands in for a heading and the cards under it.
    return (
      <RouteSkeleton
        label={t('common:status.loading')}
        rows={6}
        columns={4}
        filters={0}
        action={false}
      />
    );
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('home:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('home:error.body')}</p>
        <Button variant="secondary" onClick={() => void query.refetch()}>
          {t('home:error.retry')}
        </Button>
      </section>
    );
  }

  return <HomeView overview={query.data} />;
}
